import { cn } from "@/lib/utils";
import { ReactNode, useState } from "react";
import { usePlausible } from "next-plausible";
import { PLAUSIBLE_GOAL_KEYS } from "@/@core/constants/plausible";
import { WHATSAPP_SECTIONS } from "@/@core/enums/whatsapp_tags.enum";
import { useSite } from "@/lib/hooks/site";
import dynamic from "next/dynamic";

const WhatsappQrModal = dynamic(() => import("@/components/WhatsappQrModal"));

interface Props {
  title: string;
  href: string;
  children: ReactNode;
  venueId?: number;
  section?: WHATSAPP_SECTIONS;
  className?: string;
  disabled?: boolean;
  extraClickHandler?: () => void;
}

function WhatsappLinkWithBeacon({
  title,
  href,
  venueId,
  children,
  className,
  disabled,
  extraClickHandler,
  section,
}: Props) {
  const site = useSite();
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const plausible = usePlausible();

  const sendWhatsAppStats = () => {
    if (!venueId && !section) return;

    if (navigator.sendBeacon) {
      const url = `https://www.${site.domain}/api/whatsapp-button`;
      const data = JSON.stringify({
        venueId: venueId,
        section,
      });
      navigator.sendBeacon(url, data);
    }
  };

  const handleClick = () => {
    if (!venueId && !section) return;

    plausible(PLAUSIBLE_GOAL_KEYS.VENUE_WHATSAPP_CLICK_EVENT);

    sendWhatsAppStats();

    if (extraClickHandler && typeof extraClickHandler == "function")
      extraClickHandler();
  };

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    if (window.innerWidth > 1024) {
      e.preventDefault();
      setIsQrModalOpen(true);
    } else {
      handleClick();
    }
  };

  const handleOpenWhatsappButton = () => {
    if (venueId) {
      plausible(PLAUSIBLE_GOAL_KEYS.VENUE_WHATSAPP_CLICK_EVENT);

      sendWhatsAppStats();
    }

    setIsQrModalOpen(true);
  };

  return (
    <>
      {isQrModalOpen && (
        <WhatsappQrModal
          isOpen={isQrModalOpen}
          onClose={() => setIsQrModalOpen(false)}
          href={href}
        />
      )}

      {disabled ? (
        <div className="opacity-40 cursor-not-allowed">{children}</div>
      ) : (
        <>
          <a
            title={title}
            href={href}
            onClick={handleLinkClick}
            className={cn(
              className,
              disabled ? "cursor-not-allowed opacity-40" : "",
              "md:hidden",
            )}
          >
            {children}
          </a>
          <div
            className={cn(
              className,
              disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer",
              "max-md:hidden",
            )}
            onClick={() => handleOpenWhatsappButton()}
          >
            {children}
          </div>
        </>
      )}
    </>
  );
}

export default WhatsappLinkWithBeacon;
